import type { ReactNode } from 'react';
import {
  SETTINGS_ROLES,
  type ContactInfo,
  type CustomRole,
  type DashboardView,
  type FormMapping,
  type RightsMap,
  type SettingsRole,
  type UserGroup,
  type UserHours,
  type UserRights,
  type UserTitle,
  type HolidayDates,
} from '@ehr/shared';
import type { DdOption } from '../components/Dropdown.js';

/** Users & Profile (ehr#60) — types, helpers and icons shared by its windows. */

export interface TeamUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: SettingsRole;
  active: boolean;
  /** ehr#232 - a provider (sees clients) or support staff. */
  seesClients?: boolean;
  /** ehr#320 - their Supervision setting (Provider Profile → Forms and Supervision). */
  supervision?: { required: boolean; supervisorId: string | null };
  /** ehr#320 - a pre-licensed associate (can't co-sign). */
  associate?: boolean;
  title: string | null;
  customRoleId: string | null;
  hours: UserHours | null;
  color: string | null;
  rights: UserRights | null;
  dashboardView: DashboardView | null;
  contactInfo: ContactInfo | null;
}
export interface Team {
  roles: CustomRole[];
  titles: UserTitle[];
  /** Every role key's rights, defaults filled in by the server. */
  roleRights: Record<string, RightsMap>;
  groups: UserGroup[];
  dashboardView: DashboardView | null;
  formMappings: FormMapping[];
  rightsMapping: string;
}
export const EMPTY_TEAM: Team = {
  roles: [],
  titles: [],
  roleRights: {},
  groups: [],
  dashboardView: null,
  formMappings: [],
  rightsMapping: '',
};
/** A user's avatar colour: their chosen one, else a stable colour from their id. */
export const userColor = (u: TeamUser) => u.color || color(u.id);
/** A company holiday (ehr#376: a date, a repeating rule, or one day only; see HolidayDates). */
export interface Holiday extends HolidayDates {
  name: string;
}

export const ROLE_LABEL: Record<SettingsRole, string> = {
  admin: 'Admin',
  manager: 'Manager',
  clinician: 'Clinician',
  supervisor: 'Supervisor',
  front_desk: 'Front desk',
};

export const uname = (u: TeamUser) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
export const initials = (u: TeamUser) =>
  ((u.firstName?.[0] ?? '') + (u.lastName?.[0] ?? '')).toUpperCase() ||
  u.email[0]?.toUpperCase() ||
  'U';
export const AV = ['#1a8a66', '#0e7490', '#7c3aed', '#b45309', '#c0392b', '#3b72d9'];
export const color = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AV[h % AV.length]!;
};
export const newId = (p: string) =>
  `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
export const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** The role dropdown value: a built-in role, or a custom role by id. */
export const roleValue = (u: TeamUser, team: Team) =>
  u.customRoleId && team.roles.some((r) => r.id === u.customRoleId)
    ? `cr:${u.customRoleId}`
    : `sys:${u.role}`;
export const roleName = (u: TeamUser, team: Team) =>
  team.roles.find((r) => r.id === u.customRoleId)?.name ?? ROLE_LABEL[u.role] ?? u.role;
export const roleOptions = (team: Team): DdOption[] => [
  ...SETTINGS_ROLES.map((r) => ({ value: `sys:${r}`, label: ROLE_LABEL[r] })),
  ...team.roles.map((r) => ({
    value: `cr:${r.id}`,
    label: r.name,
    search: ROLE_LABEL[r.baseRole],
  })),
];
/** Dropdown value → PATCH body. */
export const roleBody = (v: string, team: Team) => {
  if (v.startsWith('cr:')) {
    const cr = team.roles.find((r) => r.id === v.slice(3));
    return cr ? { role: cr.baseRole, customRoleId: cr.id } : null;
  }
  return { role: v.slice(4) as SettingsRole, customRoleId: null };
};

// ── icons (the mockup's) ─────────────────────────────────────────────────────
export const Svg = ({
  children,
  size,
  sw = 2,
}: {
  children: ReactNode;
  size?: number;
  sw?: number;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={sw}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {children}
  </svg>
);
export const IconX = (
  <Svg size={18}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </Svg>
);
export const IconXSm = (
  <Svg size={16} sw={2.1}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </Svg>
);
export const IconPencil = (
  <Svg size={15}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </Svg>
);
export const IconPlus = (
  <Svg size={13} sw={2.4}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </Svg>
);
export const IconShield = (
  <Svg size={13} sw={2.2}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </Svg>
);
export const IconIdCard = (
  <Svg size={13} sw={2.2}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="9" cy="10.5" r="2" />
    <path d="M5.2 16.5c.3-1.6 1.9-2.6 3.8-2.6s3.5 1 3.8 2.6" />
    <line x1="15" y1="9.5" x2="19" y2="9.5" />
    <line x1="15" y1="13.5" x2="18" y2="13.5" />
  </Svg>
);
export const IconClock = (
  <Svg>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7.5 12 12 15.5 14" />
  </Svg>
);
export const IconSearch = (
  <svg
    className="user-search-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
export const HANDLE = '⁙⁙';

// ── Sub-modal shell (User Roles / User Titles / Add User) ────────────────────
export function Shell({
  title,
  sub,
  onClose,
  children,
  foot,
  className,
}: {
  title: string;
  sub?: string;
  onClose: () => void;
  children: ReactNode;
  foot: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`modal-lg modal-wide um-modal${className ? ` ${className}` : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="modal-head">
        <div>
          <h2>{title}</h2>
          {sub && <p>{sub}</p>}
        </div>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          {IconX}
        </button>
      </div>
      <div className="modal-body tr-modal">{children}</div>
      <div className="modal-foot">{foot}</div>
    </div>
  );
}
